import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import useTrapSpiderfy from '@App/hooks/useTrapSpiderfy'

import { setHoveredItem, setClickedItem, setHoveredClusterTrapIds } from '../mosquito-observations.slice'

import './mosquito-observations-detection-map-logic.styl'

function getObservationId(observation) {
  return observation?.trapId
    || observation?.id
    || observation?.properties?.locationKey
    || observation?.properties?.client_name
}

function getObservationHoverKey(observation) {
  const source = observation?.source || observation?.properties?.source || 'samples'
  const id = getObservationId(observation)

  return id ? `${source}-${id}` : null
}

function findObservationByHoverKey(observations, hoverKey) {
  return (observations || []).find(observation => getObservationHoverKey(observation) === hoverKey)
}

function clearSource(map, sourceId) {
  const source = map?.getSource(sourceId)
  if (!source) return

  source.setData({
    type: 'FeatureCollection',
    features: [],
  })
}

function observationToMapFeature(observation) {
  const { geometry, properties = {}, status } = observation
  const observationId = getObservationId(observation)
  const hoverKey = getObservationHoverKey(observation)

  return {
    type: 'Feature',
    geometry,
    properties: {
      hoverKey,
      observationId,
      locationKey: properties.locationKey,
      client_name: properties.client_name,
      sampleCount: properties.sampleCount,
      status: status || properties.status || 'inactive',
    },
  }
}

function MosquitoObservationsDetectionMapLogic({ map, observations }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const observationsRef = useRef(observations)
  const reapplyHoveredRef = useRef(null)
  const lastHoveredClusterIdRef = useRef(null)

  const { hoveredItem } = useSelector(state => state.mosquitoDetection)

  useEffect(() => {
    observationsRef.current = observations
  }, [observations])

  useTrapSpiderfy({
    map,
    closeOnLeafClick: false,
    onLeafClick: (feature) => {
      const canvasContainer = document.querySelector('.maplibregl-canvas')
      canvasContainer?.classList.remove('pointer-cursor')

      const hoverKey = feature?.properties?.hoverKey
      const clickedTrap = findObservationByHoverKey(observationsRef.current, hoverKey)
      if (!clickedTrap) return

      dispatch(setClickedItem(clickedTrap))
      navigate(`/mosquito-observations/detections`)
    },
    onLeafHover: (feature) => {
      if (feature === undefined) return
      const canvasContainer = document.querySelector('.maplibregl-canvas')
      if (!feature) {
        canvasContainer?.classList.remove('pointer-cursor')
        dispatch(setHoveredItem(null))
        return
      }

      const hoverKey = feature?.properties?.hoverKey
      const hoveredTrap = findObservationByHoverKey(observationsRef.current, hoverKey)

      if (hoveredTrap) {
        canvasContainer?.classList.add('pointer-cursor')
        dispatch(setHoveredItem(hoveredTrap))
      }
    },
  })

  useEffect(() => {
    if (!map) return

    const source = map.getSource('traps')
    if (!source) return

    source.setData({
      type: 'FeatureCollection',
      features: (observations || []).map(observationToMapFeature),
    })

    return () => {
      const currentSource = map.getSource('traps')
      if (currentSource) {
        currentSource.setData({
          type: 'FeatureCollection',
          features: [],
        })
      }
    }
  }, [map, observations])

  useEffect(() => {
    if (!map) return

    const hoveredSource = map.getSource('hovered-trap')
    if (!hoveredSource) return
    let isMounted = true

    const hoveredKey = getObservationHoverKey(hoveredItem)
    const hoveredTrap = findObservationByHoverKey(observations, hoveredKey)

    if (!hoveredTrap) {
      clearSource(map, 'hovered-trap')
      return
    }

    const hoveredFeature = observationToMapFeature(hoveredTrap)

    const apply = async () => {
      const renderedFeatures = map.queryRenderedFeatures({ layers: ['trap-layer'] })
      const directFeature = renderedFeatures.find(f => f.properties?.hoverKey === hoveredKey)

      if (directFeature) {
        if (!isMounted) return
        hoveredSource.setData({
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            geometry: hoveredFeature.geometry,
            properties: hoveredFeature.properties,
          }],
        })
        return
      }

      const trapsSource = map.getSource('traps')
      const clusterFeatures = renderedFeatures.filter(f => f.properties?.point_count)

      for (const cluster of clusterFeatures) {
        const clusterId = cluster.properties.cluster_id
        const leaves = await trapsSource.getClusterLeaves(clusterId, Infinity, 0).catch(() => [])

        if (leaves.some(leaf => leaf.properties?.hoverKey === hoveredKey)) {
          if (!isMounted) return
          hoveredSource.setData({
            type: 'FeatureCollection',
            features: [{
              type: 'Feature',
              geometry: cluster.geometry,
              properties: hoveredFeature.properties,
            }],
          })
          return
        }
      }

      if (isMounted) clearSource(map, 'hovered-trap')
    }

    reapplyHoveredRef.current = apply
    apply()

    return () => {
      isMounted = false
      reapplyHoveredRef.current = null
      clearSource(map, 'hovered-trap')
    }
  }, [hoveredItem, map, observations])

  useEffect(() => {
    if (!map) return

    const handleZoom = () => {
      clearSource(map, 'hovered-trap')
    }

    let pendingIdleHandler = null
    const handleZoomEnd = () => {
      if (pendingIdleHandler) {
        map.off('idle', pendingIdleHandler)
      }

      pendingIdleHandler = () => {
        pendingIdleHandler = null
        reapplyHoveredRef.current?.()
      }
      map.once('idle', pendingIdleHandler)
    }

    map.on('zoom', handleZoom)
    map.on('zoomend', handleZoomEnd)
    return () => {
      map.off('zoom', handleZoom)
      map.off('zoomend', handleZoomEnd)
      if (pendingIdleHandler) {
        map.off('idle', pendingIdleHandler)
      }
    }
  }, [map])

  useEffect(() => {
    if (!map) return
    const canvasContainer = document.querySelector('.maplibregl-canvas')

    const layerId = 'trap-layer'
    const handleMouseMove = (event) => {
      const hoveredFeature = event?.features?.[0]
      if (!hoveredFeature) return

      canvasContainer.classList.add('pointer-cursor')

      if (hoveredFeature.properties?.point_count) {
        const clusterId = hoveredFeature.properties.cluster_id
        if (clusterId === lastHoveredClusterIdRef.current) return
        lastHoveredClusterIdRef.current = clusterId

        const hoveredSource = map.getSource('hovered-trap')
        if (hoveredSource) {
          hoveredSource.setData({
            type: 'FeatureCollection',
            features: [{
              type: 'Feature',
              geometry: hoveredFeature.geometry,
              properties: {},
            }],
          })
        }

        const trapsSource = map.getSource('traps')
        trapsSource.getClusterLeaves(clusterId, Infinity, 0)
          .then((leaves) => {
            const trapIds = leaves.map(l => l.properties?.observationId).filter(Boolean)
            dispatch(setHoveredClusterTrapIds(trapIds))
          })
          .catch(() => {})
        return
      }

      lastHoveredClusterIdRef.current = null
      dispatch(setHoveredClusterTrapIds([]))

      const hoverKey = hoveredFeature?.properties?.hoverKey
      const hoveredTrap = findObservationByHoverKey(observations, hoverKey)
      if (!hoveredTrap) return

      dispatch(setHoveredItem(hoveredTrap))
    }

    const handleMouseLeave = () => {
      canvasContainer.classList.remove('pointer-cursor')
      lastHoveredClusterIdRef.current = null
      dispatch(setHoveredClusterTrapIds([]))
      dispatch(setHoveredItem(null))

      clearSource(map, 'hovered-trap')
    }

    const handleClick = (event) => {
      navigate(`/mosquito-observations/detections`)
      const clickedFeature = event?.features?.[0]
      if (!clickedFeature) return

      const hoverKey = clickedFeature?.properties?.hoverKey
      const clickedTrap = findObservationByHoverKey(observations, hoverKey)
      if (!clickedTrap) return

      dispatch(setClickedItem(clickedTrap))
    }

    let handlersAttached = false
    const attachHandlers = () => {
      if (handlersAttached || !map.getLayer(layerId)) return
      map.on('mousemove', layerId, handleMouseMove)
      map.on('mouseleave', layerId, handleMouseLeave)
      map.on('click', layerId, handleClick)
      handlersAttached = true
    }

    attachHandlers()
    map.on('idle', attachHandlers)
    map.on('styledata', attachHandlers)

    return () => {
      map.off('idle', attachHandlers)
      map.off('styledata', attachHandlers)
      if (handlersAttached) {
        map.off('mousemove', layerId, handleMouseMove)
        map.off('mouseleave', layerId, handleMouseLeave)
        map.off('click', layerId, handleClick)
      }
      map.getCanvas().style.cursor = ''
      lastHoveredClusterIdRef.current = null
      dispatch(setHoveredClusterTrapIds([]))
      dispatch(setHoveredItem(null))
      clearSource(map, 'hovered-trap')
    }
  }, [dispatch, navigate, map, observations])

  return null
}

export default MosquitoObservationsDetectionMapLogic
